import { useEffect, useRef } from 'react'
import type { AsyncState } from '../../platform/async/Async'
import { emit } from '../../platform/analytics/analytics'

/**
 * `rules_viewed`, once per mount of a tab: the first render in which the query
 * HAS an answer — data, stale data, or an empty list (count 0) — including one
 * already in the cache at mount. Not `<Async announce>`, which runs only on a
 * loading → data edge, so it misses an empty list and a warm mount, and speaks.
 * A later refetch changes `count`, not the ref, so it emits nothing.
 */
export function useViewedOnce<T>(
  state: AsyncState<readonly T[]>,
  surface: 'rules' | 'suggestions',
  enabled = true,
) {
  const count =
    state.status === 'empty' ? 0 : state.status === 'success' || state.status === 'stale' ? state.data.length : undefined
  const fired = useRef(false)
  useEffect(() => {
    if (!enabled || fired.current || count === undefined) return
    fired.current = true
    emit(
      'rules_viewed',
      surface === 'rules' ? { rule_count: count, surface } : { item_count: count, surface },
    )
  }, [count, surface, enabled])
}
